import { useEffect, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Bell,
  BookOpen,
  CalendarCheck,
  CircleCheck,
  CirclePause,
  CircleX,
  Clock,
  HardDrive,
  Image,
  ImageMinus,
  MessageSquare,
  RefreshCw,
  TriangleAlert,
  UserMinus,
  UserPlus,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useSocket } from '../hooks/useSocket';
import type { AppNotification } from '../types';
import { playNotificationSound } from '../utils/sound';
import { closeBrowserNotifications, notificationTag, showBrowserNotification } from '../utils/browserNotify';
import { filesLabel, groupNotifications, notificationTone, relativeTime, type NotificationGroup } from '../utils/notificationView';

const TYPE_ICONS: Record<string, LucideIcon> = {
  ORDER_COMPLETED: CircleCheck,
  ORDER_PAUSED: CirclePause,
  ORDER_CANCELLED: CircleX,
  ORDER_REQUEST: Clock,
  ORDER_REQUEST_DENIED: CircleX,
  SCHEDULE_CHANGED: Clock,
  REMINDER: Clock,
  LATE: Clock,
  MEDIA_ADDED: Image,
  MEDIA_REMOVED: ImageMinus,
  CHAT_MESSAGE: MessageSquare,
  ORDER_ASSIGNED: UserPlus,
  ORDER_UNASSIGNED: UserMinus,
  ORDER_UPDATED: RefreshCw,
  BRIGADE_NOT_APPLIED: TriangleAlert,
  WRONG_SELLER_ENTITY: TriangleAlert,
  COUNTERPARTY_ALERT: TriangleAlert,
  KB_UPDATED: BookOpen,
  KB_SUGGESTION: BookOpen,
  MAINTENANCE_DEALS: CalendarCheck,
  STORAGE_LOW: HardDrive,
};

// Прочитанных сервер отдаёт до 50 вместе с новыми — в списке хватит последних
const READ_GROUPS_SHOWN = 20;

export default function NotificationsBell() {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const socket = useSocket('notifications');
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  function load() {
    apiClient
      .get<AppNotification[]>('/notifications')
      .then(({ data }) => {
        // Прочитанные тоже показываем — отдельным блоком под новыми (16.10.2026): раньше
        // просмотренное уведомление пропадало совсем, и к наряду из него было не вернуться
        setNotifications(data);
        setLoadError(false);
      })
      .catch((err) => {
        console.error('Не удалось загрузить уведомления', err);
        setLoadError(true);
      });
  }

  useEffect(load, []);

  useEffect(() => {
    if (!socket) return;
    const onNotification = (n: AppNotification) => {
      setNotifications((prev) => [n, ...prev]);
      // Сообщения чата (личного и общих каналов) уже озвучивает и показывает Layout.tsx —
      // он слушает сырой сокет чата напрямую и покрывает все случаи (не только те, на кого
      // формально заведено уведомление). Если играть звук/показывать тост ещё и здесь —
      // получится два уведомления на одно сообщение
      if (n.type !== 'CHAT_MESSAGE') {
        playNotificationSound();
        showBrowserNotification(n.title, n.body, notificationTag(n));
      }
    };
    // Прочитано в другом месте (открыл наряд, прочитал чат, другая вкладка) — переносим в
    // «Прочитанные» и здесь, а всплывающие уведомления Windows закрываем по той же метке
    const onRead = (r: { tag?: string; ids?: string[]; all?: boolean }) => {
      if (r.all) {
        setNotifications((prev) => markRead(prev, () => true));
        closeBrowserNotifications();
        return;
      }
      if (r.ids) {
        const ids = new Set(r.ids);
        setNotifications((prev) => markRead(prev, (n) => ids.has(n.id)));
      }
      if (r.tag) {
        setNotifications((prev) => markRead(prev, (n) => notificationTag(n) === r.tag));
        closeBrowserNotifications(r.tag);
      }
    };
    socket.on('notification', onNotification);
    socket.on('notifications:read', onRead);
    return () => {
      socket.off('notification', onNotification);
      socket.off('notifications:read', onRead);
    };
  }, [socket]);

  // «5 мин назад» пересчитываем, пока список открыт
  useEffect(() => {
    if (!open) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [open]);

  // Раньше отметка "непрочитано" гасла в тот же миг, как диспетчер открывал список — он
  // ещё не успевал прочитать ни одного сообщения. Теперь список остаётся подсвеченным, пока
  // открыт, а прочитанным всё помечается только когда диспетчер его закрывает (успел увидеть)
  // Гасим всё одним запросом. Раньше здесь был запрос на каждое непрочитанное — при
  // накопившемся хвосте закрытие колокольчика давало залп в сотни запросов, лимитер
  // (100 в минуту на IP) отбивал лишние, и весь офис за одним адресом получал
  // «Не удалось…» на любой запрос в ту же минуту.
  // Ошибку больше не глушим молча: если сервер отказал, возвращаем отметки как были —
  // иначе на экране «прочитано», а в базе нет, и при следующем входе всё всплывёт заново
  function markSeenAsRead() {
    setNotifications((prev) => {
      const unreadIds = new Set(prev.filter((n) => !n.readAt).map((n) => n.id));
      if (unreadIds.size === 0) return prev;
      apiClient.patch('/notifications/read-all').catch((err) => {
        console.error('Не удалось отметить уведомления прочитанными', err);
        // Откатываем только те, что сами пометили, — пришедшие за это время не трогаем
        load();
      });
      // Просмотренные уходят в «Прочитанные» — счётчик на колокольчике гаснет
      return markRead(prev, () => true);
    });
  }

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen((wasOpen) => {
          if (wasOpen) markSeenAsRead();
          return false;
        });
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleOpen() {
    setOpen((wasOpen) => {
      if (wasOpen) markSeenAsRead();
      return !wasOpen;
    });
  }

  function handleNotificationClick(n: AppNotification) {
    if (!n.orderId) return;
    markSeenAsRead();
    setOpen(false);
    navigate(`/orders/${n.orderId}`);
  }

  const unreadCount = notifications.filter((n) => !n.readAt).length;
  const unreadGroups = groupNotifications(notifications.filter((n) => !n.readAt));
  const readGroups = groupNotifications(notifications.filter((n) => n.readAt)).slice(0, READ_GROUPS_SHOWN);

  // Одна строка: значок типа, что случилось (×N, если подряд несколько одинаковых), когда;
  // ниже короткий адрес и кто сделал. Полный текст — во всплывающей подсказке
  function renderGroup(g: NotificationGroup) {
    const n = g.first;
    const Icon = TYPE_ICONS[n.type] ?? Bell;
    const { who, address, details, files } = g.view;
    const meta = [who, files != null ? `+${filesLabel(files)}` : undefined, details].filter(Boolean).join(' · ');
    return (
      <div
        key={n.id}
        className={`notification-item ${!n.readAt ? 'unread' : ''} ${n.orderId ? 'clickable' : ''}`}
        onClick={n.orderId ? () => handleNotificationClick(n) : undefined}
        role={n.orderId ? 'button' : undefined}
        title={n.body}
      >
        <span className={`notification-icon tone-${notificationTone(n.type)}`} aria-hidden>
          <Icon size={16} strokeWidth={2.2} />
        </span>
        <div className="notification-main">
          <div className="notification-head">
            <span className="notification-title">{n.title}</span>
            {g.items.length > 1 && <span className="notification-repeat">×{g.items.length}</span>}
            <span className="notification-time">{relativeTime(n.createdAt, now)}</span>
          </div>
          {address && <div className="notification-address">{address}</div>}
          {meta && <div className="notification-body">{meta}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-bell" ref={containerRef}>
      <button
        className={`bell-button ${unreadCount > 0 ? 'has-unread' : ''} ${open ? 'open' : ''}`}
        onClick={handleOpen}
        aria-label={unreadCount > 0 ? `Уведомления: новых ${unreadCount}` : 'Уведомления'}
        title="Уведомления"
      >
        <Bell size={18} strokeWidth={2.2} />
        {/* Счётчик — внутри кнопки, а не кружком поверх угла: не налезает и не обрезается */}
        {unreadCount > 0 && <span className="bell-count">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>
      {open && (
        <div className="notifications-dropdown">
          <div className="notifications-header">
            <span>Уведомления</span>
            {unreadCount > 0 && (
              <button className="notifications-read-all" onClick={markSeenAsRead}>
                Прочитать все
              </button>
            )}
          </div>
          <div className="notifications-list">
            {loadError && <p className="error-text notifications-empty">Не удалось загрузить уведомления</p>}
            {!loadError && unreadGroups.length === 0 && <p className="muted notifications-empty">Новых уведомлений нет</p>}
            {unreadGroups.length > 0 && <div className="notifications-section">Новые</div>}
            {unreadGroups.map(renderGroup)}
            {readGroups.length > 0 && <div className="notifications-section">Прочитанные</div>}
            {readGroups.map(renderGroup)}
          </div>
        </div>
      )}
    </div>
  );
}

/** Отметить прочитанными на экране — время прочтения ставим своё, точное знает сервер */
function markRead(list: AppNotification[], match: (n: AppNotification) => boolean): AppNotification[] {
  const readAt = new Date().toISOString();
  let changed = false;
  const next = list.map((n) => {
    if (n.readAt || !match(n)) return n;
    changed = true;
    return { ...n, readAt };
  });
  return changed ? next : list;
}
